import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageContainer } from '../../../common/components/PageContainer';
import { PeriodControl, PeriodValue, fitPeriod, resolvePeriod } from '../../../components/design-system/analytics';
import { HistoryLimitNotice, useHistoryWindow } from '../../../components/design-system/plan/HistoryLimitNotice';
import { VizHeader } from '../../../modules/vendor/analytics/viz/components/VizHeader';
import { AnalyticsTabs, useAnalyticsTab, useAnalyticsTabs } from './components/AnalyticsTabs';
import ModernAnalyticsDashboard from './modern-analytics.page';
import ProductPerformancePage from './product-performance.page';
import AssistantInsightsPage from './assistant-insights.page';

/**
 * Visualizer analytics: the overview (who came, what they did, where they
 * left), product performance (which products work) and the assistant (what
 * shoppers asked for). One period for all tabs, so switching tab keeps the dates.
 */
const AnalyticsPage: React.FC = () => {
  const { t } = useTranslation();
  const [requested] = useAnalyticsTab();
  // A link to the assistant tab, for a vendor without the assistant, lands on the overview.
  const tab = useAnalyticsTabs().includes(requested) ? requested : 'overview';
  const [range, setRange] = useState<PeriodValue>({ key: '30d' });
  // Free reads 30 days back: longer presets stay on the control, marked Pro.
  const { maxDays } = useHistoryWindow();
  const shown = useMemo(() => fitPeriod(range, maxDays) as PeriodValue, [range, maxDays]);
  const period = useMemo(() => resolvePeriod(shown.key, { start: shown.start, end: shown.end }), [shown]);

  return (
    <PageContainer breadcrumbs={[{ label: t('SIDEBAR.VISUALIZER.TITLE') }, { label: t('SIDEBAR.ENGAGEMENT.ANALYTICS') }]}>
      <div className="mx-auto flex max-w-[1200px] flex-col gap-6" data-testid="viz-analytics">
        <VizHeader
          title={t(tab === 'products' ? 'VIZ_ANALYTICS.PRODUCTS.TITLE' : tab === 'assistant' ? 'VIZ_ANALYTICS.ASSISTANT.TITLE' : 'VIZ_ANALYTICS.ANALYTICS.TITLE')}
          description={t(tab === 'products' ? 'VIZ_ANALYTICS.PRODUCTS.SUBTITLE' : tab === 'assistant' ? 'VIZ_ANALYTICS.ASSISTANT.SUBTITLE' : 'VIZ_ANALYTICS.ANALYTICS.SUBTITLE')}
          actions={<PeriodControl value={shown} onChange={setRange} maxDays={maxDays} />}
        />
        <HistoryLimitNotice period={period} />
        <AnalyticsTabs />
        {tab === 'products' ? <ProductPerformancePage period={period} />
          : tab === 'assistant' ? <AssistantInsightsPage period={period} />
          : <ModernAnalyticsDashboard period={period} />}
      </div>
    </PageContainer>
  );
};

export default AnalyticsPage;
